import { AuthService } from '../../services/auth';
import { academyProfileService } from '../../services/academyProfile';
import { LEGAL_VERSIONS, type ManualInviteAccountFor } from '@court-sync/shared';
import type { UserFormData } from './publicBooking/schemas';

export type PasswordResetSender = (email: string, returnUrl: string) => Promise<void>;

/** Always resolves; callers show generic success (no email enumeration). */
export async function requestPasswordResetWithFallback(
  email: string,
  primaryContinueUrl: string,
  fallbackContinueUrl: string,
  sendReset: PasswordResetSender = AuthService.requestPasswordResetEmail.bind(AuthService)
): Promise<void> {
  try {
    await sendReset(email, primaryContinueUrl);
  } catch {
    if (fallbackContinueUrl !== primaryContinueUrl) {
      try {
        await sendReset(email, fallbackContinueUrl);
      } catch {
        // Still show generic success
      }
    }
  }
}

export type AcademyRegisterSubmitInput = {
  data: UserFormData;
  academyId: string;
  academySlug: string;
  dashboardReturnPath: string;
  manualInviteToken?: string | null;
  manualInviteAccountFor?: ManualInviteAccountFor | null;
};

export type AcademyRegisterSubmitResult =
  | {
      type: 'verification';
      email: string;
      returnUrl: string;
      academySlug: string;
      verificationEmailFailed?: boolean;
    }
  | { type: 'login'; email: string; message: string }
  | { type: 'error'; message: string };

type SignUpFn = typeof AuthService.signUpAsGuardian;
type CreateProfileFn = typeof academyProfileService.createProfile;

export async function submitAcademyRegister(
  input: AcademyRegisterSubmitInput,
  signUp: SignUpFn = AuthService.signUpAsGuardian.bind(AuthService),
  createProfile: CreateProfileFn = academyProfileService.createProfile.bind(academyProfileService)
): Promise<AcademyRegisterSubmitResult> {
  const { data, academyId, academySlug, dashboardReturnPath, manualInviteToken, manualInviteAccountFor } =
    input;

  const trimmedInvite = manualInviteToken?.trim() || '';
  const inviteAccountFor =
    manualInviteAccountFor === 'myself' || manualInviteAccountFor === 'child'
      ? manualInviteAccountFor
      : undefined;
  const signupRole: 'guardian' | 'student' =
    trimmedInvite && inviteAccountFor === 'child' ? 'guardian' : 'student';

  try {
    const { verificationEmailFailed } = await signUp(
      data.email,
      data.password,
      data.firstName,
      data.lastName,
      dashboardReturnPath,
      {
        termsAccepted: data.termsAccepted,
        privacyAccepted: data.privacyAccepted,
        termsVersion: LEGAL_VERSIONS.TERMS,
        privacyVersion: LEGAL_VERSIONS.PRIVACY,
        marketingEmailConsent: data.marketingEmailConsent || false,
      },
      academyId,
      signupRole,
      {
        ...(data.phone?.trim() && { phone: data.phone.trim() }),
        ...(trimmedInvite && { manualInviteToken: trimmedInvite }),
        ...(inviteAccountFor && { manualInviteAccountFor: inviteAccountFor }),
      }
    );

    try {
      await createProfile({
        academySlug,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        ...(data.phone?.trim() && { phone: data.phone.trim() }),
        ...(trimmedInvite && { manualInviteToken: trimmedInvite }),
        ...(inviteAccountFor && { manualInviteAccountFor: inviteAccountFor }),
        ensureSelfStudentRosterEntry: true,
      });
    } catch (profileError) {
      console.warn('Academy profile create after signup failed (may already exist):', profileError);
    }

    return {
      type: 'verification',
      email: data.email,
      returnUrl: dashboardReturnPath,
      academySlug,
      verificationEmailFailed,
    };
  } catch (authError: unknown) {
    const err = authError as { code?: string; message?: string };
    if (err.code === 'auth/email-already-in-use') {
      return {
        type: 'login',
        email: data.email,
        message: 'An account with this email already exists. Please sign in to continue.',
      };
    }
    return { type: 'error', message: err.message || 'Could not create account' };
  }
}
